import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Lock, LockOpen, AlertCircle, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { formatCurrency as sharedFormatCurrency } from '../utils/currency';
import { getLocationsByOrgId } from '../utils/locationApi';
import { getJournalDaySummary } from '../utils/journalExportApi';
import { closeJournalDays, reopenJournalDays } from '../utils/journalCloseoutsApi';
import LoadingScreen from '../components/LoadingScreen';

// Exact design-system semantic colors (Tailwind neutralizes blue/sky/etc → onyx,
// so status hues are applied as inline styles — same approach as the detail page).
const SUCCESS = { ink: '#15803D', bg: '#ECFDF3', border: '#A6F4C5' };
const DANGER = { ink: '#B42318', bg: '#FEF3F2', border: '#FECDCA' };

// Display conventions match the detail page: revenue/tax/ledger are stored
// credit-positive and shown as stored; payments are stored debit-negative and
// shown positive; the out-of-balance residual is shown debit-positive (-net).
const displayCategory = (cat, net) => (cat === 'payments' ? -net : net);

// Sentinel for the portfolio view — one summary row per location for the month.
const ALL_LOCATIONS = { id: 'all', name: 'All locations' };

const normalizeCurrencyCode = (value, fallback = 'USD') => {
  const upper = String(value || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(upper) ? upper : fallback;
};

const monthKey = (d) => d.toISOString().slice(0, 7);
const monthRange = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const start = `${ym}-01`;
  const end = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); // last day of month
  return { start, end };
};
const fmtMonthLabel = (ym) =>
  new Date(`${ym}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const fmtDay = (iso) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', weekday: 'short', timeZone: 'UTC' });
const fmtStampDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const StatusChip = ({ closeout }) => (
  closeout?.status === 'closed' ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full text-[11.5px] font-semibold bg-slate-900 text-white">
      <Lock size={11} /> Closed
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full text-[11.5px] font-semibold" style={{ background: SUCCESS.bg, color: SUCCESS.ink }}>
      <LockOpen size={11} /> Open
    </span>
  )
);

const JournalOverviewPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization, isLoading: isLoadingUserSettings, userSettings } = useUserSettingsContext();
  const journalExportEnabled = useFeatureFlagEnabled('journal-export');

  // ---- Permission gates (same derivation as the detail page) ----
  const journalPermission = (() => {
    const p = currentOrganization?.access?.journalPermission
      ?? userSettings?.orgAccess?.[currentOrganization?.code]?.journalPermission;
    return ['read', 'edit', 'write'].includes(p) ? p : null;
  })();
  const canWriteJournal = journalPermission === null || journalPermission === 'write';
  // Org admin — the only role that can reopen a closed day. Prefer the org
  // payload from initData (reflects the EMULATED user too).
  const orgRole = currentOrganization?.access?.role
    ?? userSettings?.orgAccess?.[currentOrganization?.code]?.role;
  const isOrgAdmin = userSettings?.isAdmin === 1 || orgRole === 'orgAdmin' || orgRole === 'superAdmin';

  // Snapshot URL filters once so refresh / back-forward restore the view.
  const urlInit = useRef(null);
  if (urlInit.current === null) {
    urlInit.current = {
      locationId: searchParams.get('location'),
      month: /^\d{4}-\d{2}$/.test(searchParams.get('month') || '') ? searchParams.get('month') : null,
    };
  }

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [month, setMonth] = useState(urlInit.current.month || monthKey(new Date()));
  const [summary, setSummary] = useState(null); // { days, totals, currency }
  // All-locations view: [{ location, totals, currency, error }] in org reporting currency
  const [allSummaries, setAllSummaries] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mutatingDate, setMutatingDate] = useState(null); // day being closed/reopened ('month' for bulk)

  useEffect(() => {
    if (journalExportEnabled === false) navigate('/dashboard');
  }, [journalExportEnabled, navigate]);

  // Load locations
  useEffect(() => {
    if (!currentOrganization || isLoadingUserSettings) return;
    getLocationsByOrgId(currentOrganization.id)
      .then(data => {
        if (data?.length) {
          setLocations(data);
          const fromUrl = urlInit.current.locationId === 'all'
            ? ALL_LOCATIONS
            : urlInit.current.locationId
              ? data.find(l => String(l.id) === String(urlInit.current.locationId))
              : null;
          // Default to the portfolio view — single-location orgs go straight
          // to their only property's day table.
          setSelectedLocation(fromUrl || (data.length > 1 ? ALL_LOCATIONS : data[0]));
        } else {
          setError('No locations found for this organization.');
        }
      })
      .catch(err => setError(`Failed to load locations: ${err.message}`));
  }, [currentOrganization, isLoadingUserSettings]);

  // Mirror filters into the URL (replace — no history spam).
  useEffect(() => {
    if (!selectedLocation?.id) return;
    setSearchParams({ location: String(selectedLocation.id), month }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, month]);

  // Close the location dropdown on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (showLocationDropdown && !e.target.closest('.location-dropdown-container')) setShowLocationDropdown(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showLocationDropdown]);

  const { start, end } = monthRange(month);
  const isAllView = selectedLocation?.id === 'all';
  const orgCurrency = normalizeCurrencyCode(currentOrganization?.reportingCurrency);

  const fetchSummary = async () => {
    if (!selectedLocation?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      if (isAllView) {
        // Portfolio: one summary per location, all converted to the org
        // reporting currency so the rows are comparable/summable.
        const results = await Promise.all(locations.map(async (loc) => {
          try {
            const r = await getJournalDaySummary({ locationId: loc.id, startDate: start, endDate: end, targetCurrency: orgCurrency });
            return { location: loc, totals: r.totals, currency: r.currency, error: null };
          } catch (err) {
            return { location: loc, totals: null, currency: null, error: err.response?.data?.error || err.message };
          }
        }));
        setAllSummaries(results);
        setSummary(null);
      } else {
        const result = await getJournalDaySummary({ locationId: selectedLocation.id, startDate: start, endDate: end });
        setSummary(result);
        setAllSummaries(null);
      }
    } catch (err) {
      setError(`Failed to load journal overview: ${err.message}`);
      setSummary(null);
      setAllSummaries(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedLocation?.id) return undefined;
    if (selectedLocation.id === 'all' && locations.length === 0) return undefined;
    const t = setTimeout(fetchSummary, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, month, locations.length]);

  const currency = isAllView
    ? orgCurrency
    : (summary?.currency?.targetCurrency || summary?.currency?.locationCurrency || selectedLocation?.currency || 'USD');
  const formatCurrency = (v) => sharedFormatCurrency(v || 0, currency);
  const days = useMemo(() => summary?.days || [], [summary]);
  const totals = summary?.totals || null;
  const hasLedger = useMemo(() => days.some(d => Math.abs(d.categories?.ledger || 0) > 0.004), [days]);
  const todayKey = new Date().toISOString().slice(0, 10);

  // Portfolio aggregates for the all-locations view (org reporting currency).
  const allRows = useMemo(() => allSummaries || [], [allSummaries]);
  const allTotals = useMemo(() => {
    if (!allSummaries) return null;
    const agg = { categories: { revenue: 0, tax: 0, payments: 0, ledger: 0 }, net: 0, closedDays: 0, dataDays: 0, missingDays: 0, errors: 0 };
    for (const r of allSummaries) {
      if (!r.totals) { agg.errors++; continue; }
      for (const [cat, v] of Object.entries(r.totals.categories || {})) {
        if (agg.categories[cat] === undefined) agg.categories[cat] = 0;
        agg.categories[cat] += v;
      }
      agg.net += r.totals.net || 0;
      agg.closedDays += r.totals.closedDays || 0;
      agg.dataDays += r.totals.dataDays || 0;
      agg.missingDays += r.totals.missingDays || 0;
    }
    return agg;
  }, [allSummaries]);
  const allHasLedger = useMemo(
    () => allRows.some(r => Math.abs(r.totals?.categories?.ledger || 0) > 0.004),
    [allRows]
  );

  const stepMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    setMonth(monthKey(new Date(Date.UTC(y, m - 1 + delta, 1))));
  };

  const goToDay = (date) => {
    navigate(`/journal-export/detail?location=${selectedLocation.id}&start=${date}&end=${date}`);
  };
  const goToDetailMonth = () => {
    navigate(`/journal-export/detail?location=${selectedLocation.id}&start=${start}&end=${end}`);
  };

  const mutateCloseout = async (label, fn) => {
    setMutatingDate(label);
    setError(null);
    try {
      const res = await fn();
      // Out-of-balance days can't be closed — surface any the server skipped.
      if (res?.skipped?.length) {
        setError(`${res.skipped.length} day${res.skipped.length === 1 ? '' : 's'} could not be closed (out of balance): ${res.skipped.map(s => s.date).join(', ')}`);
      }
      await fetchSummary();
    } catch (err) {
      setError(`Action failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setMutatingDate(null);
    }
  };
  const handleCloseDay = (date) => mutateCloseout(date, () =>
    closeJournalDays({ orgId: currentOrganization.id, locationId: selectedLocation.id, dates: [date] }));
  const handleReopenDay = (date) => {
    if (!window.confirm(`Reopen ${fmtDay(date)}? The day becomes editable again for users and data feeds.`)) return;
    return mutateCloseout(date, () =>
      reopenJournalDays({ orgId: currentOrganization.id, locationId: selectedLocation.id, dates: [date] }));
  };

  if (isLoadingUserSettings) return <LoadingScreen message="Loading User Settings" />;

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto">
      {/* STICKY PAGE HEADER — same frosted treatment as the detail page */}
      <div className="sticky top-[var(--shell-sticky-top,0px)] z-30 px-7 pt-6 pb-4 flex items-end gap-5 border-b border-slate-100/60 backdrop-blur-md">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400 mb-1.5">Financial Tools · Journal Export</div>
          <h1 className="text-[26px] font-bold tracking-[-0.5px] text-slate-900 leading-tight">Journal Overview</h1>
          {(currentOrganization || selectedLocation) && (
            <div className="flex items-center gap-2 mt-1.5 text-slate-500 text-[13px]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" /></svg>
              {currentOrganization?.name}{selectedLocation ? ` · ${selectedLocation.name}` : ''}
            </div>
          )}
        </div>
        {(totals || allTotals) && (() => {
          const t = totals || allTotals;
          return (
            <div className="flex items-center gap-2 flex-wrap pb-1 pl-1">
              <span className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-full border text-[13px] font-bold bg-white border-slate-200 text-slate-600">
                {t.closedDays} of {t.dataDays} day{t.dataDays === 1 ? '' : 's'} closed
              </span>
              {t.missingDays > 0 && (
                <span className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-full border text-[13px] font-bold" style={{ background: DANGER.bg, borderColor: DANGER.border, color: DANGER.ink }}>
                  {t.missingDays} missing day{t.missingDays === 1 ? '' : 's'}
                </span>
              )}
            </div>
          );
        })()}
        <div className="flex-1" />
        {selectedLocation && !isAllView && (
          <button onClick={goToDetailMonth}
            className="h-[42px] px-[18px] flex items-center gap-2.5 bg-orange-400 text-white rounded-[11px] text-sm font-semibold whitespace-nowrap hover:bg-orange-500"
            style={{ boxShadow: '0 4px 16px rgba(255,122,0,0.24)' }}>
            Detail view
            <ArrowRight size={15} className="opacity-85" />
          </button>
        )}
      </div>

      {/* CONTENT */}
      <div className="px-7 pb-10 pt-5 flex flex-col gap-4">
        {/* FILTER TOOLBAR CARD */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-[0_1px_2px_rgba(15,15,17,0.06)] px-5 py-[18px]">
          <div className="flex items-end gap-4 flex-wrap">
            {/* Location */}
            <label className="flex flex-col gap-1.5 min-w-[220px] flex-1 max-w-[340px] relative location-dropdown-container">
              <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Location</span>
              <button
                className="w-full h-[42px] px-3.5 flex items-center justify-between border border-slate-200 rounded-[9px] bg-white text-sm font-medium text-slate-800"
                onClick={() => setShowLocationDropdown(v => !v)}
              >
                <span className="truncate">
                  {selectedLocation ? selectedLocation.name : locations.length > 0 ? 'Select Location' : 'No Locations'}
                </span>
                <ChevronDown size={15} className="text-slate-400 flex-none" />
              </button>
              {showLocationDropdown && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-[9px] shadow-xl z-50 overflow-y-auto" style={{ maxHeight: '400px' }}>
                  {locations.length > 1 && (
                    <button
                      className="block w-full text-left px-4 py-2 text-sm font-semibold border-b border-slate-100 hover:bg-slate-50"
                      onClick={() => { setSelectedLocation(ALL_LOCATIONS); setShowLocationDropdown(false); setSummary(null); setAllSummaries(null); }}
                    >
                      All locations
                    </button>
                  )}
                  {locations.map(loc => (
                    <button
                      key={loc.id}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
                      onClick={() => { setSelectedLocation(loc); setShowLocationDropdown(false); setSummary(null); setAllSummaries(null); }}
                    >
                      {loc.name}
                    </button>
                  ))}
                </div>
              )}
            </label>

            {/* Month stepper */}
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.8px] text-slate-400">Month</span>
              <div className="flex items-center h-[42px] border border-slate-200 rounded-[9px] bg-white overflow-hidden">
                <button onClick={() => stepMonth(-1)} className="h-full px-2.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50" aria-label="Previous month"><ChevronLeft size={16} /></button>
                <span className="px-2 min-w-[130px] text-center text-sm font-semibold text-slate-800 select-none">{fmtMonthLabel(month)}</span>
                <button onClick={() => stepMonth(1)} className="h-full px-2.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50" aria-label="Next month"><ChevronRight size={16} /></button>
              </div>
            </label>

            <div className="flex-1" />
            <div className="text-[12.5px] text-slate-400 pb-2.5">
              Amounts in {currency}. Click a day to open its journal detail.
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <span>{error}</span>
            <button className="ml-auto text-red-500 hover:text-red-700" onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {/* DAY TABLE */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-400 mb-3" />
                <p className="text-slate-500 text-sm">Loading journal overview…</p>
              </div>
            </div>
          ) : isAllView ? (
            allRows.length === 0 ? (
              <div className="py-14 text-center text-slate-400 text-sm">Loading portfolio summary…</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="bg-slate-50">
                    {[
                      { key: 'loc', label: 'Location', align: 'left' },
                      { key: 'revenue', label: 'Revenue', align: 'right' },
                      { key: 'payments', label: 'Payments', align: 'right' },
                      { key: 'tax', label: 'Tax', align: 'right' },
                      ...(allHasLedger ? [{ key: 'ledger', label: 'Ledger', align: 'right' }] : []),
                      { key: 'balance', label: 'Balance check', align: 'left' },
                      { key: 'closed', label: 'Days closed', align: 'left' },
                      { key: 'missing', label: 'Missing', align: 'left' },
                      { key: 'actions', label: '', align: 'right' },
                    ].map((c, ci) => (
                      <th key={c.key} className={`py-[11px] text-[11px] font-bold tracking-[0.6px] uppercase text-slate-400 whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'} ${ci === 0 ? 'pl-5 pr-4' : 'px-4'}`}>{c.label}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {allRows.map(({ location: loc, totals: t, error: rowErr }) => {
                      const open = () => { setSelectedLocation(loc); setSummary(null); setAllSummaries(null); };
                      const cats = ['revenue', 'payments', 'tax', ...(allHasLedger ? ['ledger'] : [])];
                      return (
                        <tr key={loc.id} onClick={open} className="border-t border-slate-100 group transition-colors cursor-pointer hover:bg-orange-50/50">
                          <td className="pl-5 pr-4 py-3"><span className="text-sm font-semibold text-slate-900">{loc.name}</span></td>
                          {rowErr ? (
                            <td colSpan={cats.length + 3} className="px-4 py-3 text-[12.5px]" style={{ color: DANGER.ink }}>{rowErr}</td>
                          ) : (
                            <>
                              {cats.map(cat => (
                                <td key={cat} className="px-4 py-3 text-right font-mono text-[13px] text-slate-700 whitespace-nowrap">
                                  {t?.dataDays ? formatCurrency(displayCategory(cat, Number(t.categories?.[cat] || 0))) : <span className="text-slate-300">—</span>}
                                </td>
                              ))}
                              <td className="px-4 py-3 whitespace-nowrap">
                                {!t?.dataDays ? (
                                  <span className="text-[13px] text-slate-300">—</span>
                                ) : Math.abs(t.net) <= 0.02 ? (
                                  <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: SUCCESS.ink }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    Balanced
                                  </span>
                                ) : (
                                  <span className="font-mono text-[12.5px] font-bold" style={{ color: DANGER.ink }}>Off by {formatCurrency(-t.net)}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-[12.5px] font-semibold text-slate-600 whitespace-nowrap">
                                {t?.dataDays ? `${t.closedDays} of ${t.dataDays}` : '—'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {t?.missingDays > 0 ? (
                                  <span className="inline-flex items-center px-2.5 py-[3px] rounded-full text-[11.5px] font-semibold" style={{ background: DANGER.bg, color: DANGER.ink }}>{t.missingDays} day{t.missingDays === 1 ? '' : 's'}</span>
                                ) : (
                                  <span className="text-[13px] text-slate-300">—</span>
                                )}
                              </td>
                            </>
                          )}
                          <td className="px-4 pr-5 py-3 text-right whitespace-nowrap">
                            <button onClick={(e) => { e.stopPropagation(); open(); }} title="Open this location's month"
                              className="h-[30px] w-[30px] inline-flex items-center justify-center border border-slate-200 bg-white text-slate-400 rounded-lg opacity-70 group-hover:opacity-100 transition-opacity hover:text-orange-500 hover:border-orange-200">
                              <ArrowRight size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {allTotals && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-white">
                        <td className="pl-5 pr-4 py-3.5"><span className="text-[11px] font-bold uppercase tracking-[0.6px] text-slate-500">Portfolio total · {allRows.length} location{allRows.length === 1 ? '' : 's'}</span></td>
                        {['revenue', 'payments', 'tax', ...(allHasLedger ? ['ledger'] : [])].map(cat => (
                          <td key={cat} className="px-4 py-3.5 text-right font-mono text-[13px] font-bold text-slate-900 whitespace-nowrap">
                            {formatCurrency(displayCategory(cat, Number(allTotals.categories?.[cat] || 0)))}
                          </td>
                        ))}
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {Math.abs(allTotals.net) <= 0.02 ? (
                            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold" style={{ color: SUCCESS.ink }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                              Balanced
                            </span>
                          ) : (
                            <span className="font-mono text-[12.5px] font-bold" style={{ color: DANGER.ink }}>Off by {formatCurrency(-allTotals.net)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-[12.5px] font-semibold text-slate-500 whitespace-nowrap">{allTotals.closedDays} closed</td>
                        <td className="px-4 py-3.5 text-[12.5px] font-semibold text-slate-500 whitespace-nowrap">{allTotals.missingDays > 0 ? allTotals.missingDays : ''}</td>
                        <td className="px-4 pr-5 py-3.5" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )
          ) : days.length === 0 ? (
            <div className="py-14 text-center text-slate-400 text-sm">Select a location to view its journal by day.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-slate-50">
                  {[
                    { key: 'date', label: 'Business date', align: 'left' },
                    { key: 'revenue', label: 'Revenue', align: 'right' },
                    { key: 'payments', label: 'Payments', align: 'right' },
                    { key: 'tax', label: 'Tax', align: 'right' },
                    ...(hasLedger ? [{ key: 'ledger', label: 'Ledger', align: 'right' }] : []),
                    { key: 'balance', label: 'Balance check', align: 'left' },
                    { key: 'status', label: 'Status', align: 'left' },
                    { key: 'closedBy', label: 'Closed by', align: 'left' },
                    { key: 'exported', label: 'Exported', align: 'left' },
                    { key: 'actions', label: '', align: 'right' },
                  ].map((c, ci) => (
                    <th key={c.key} className={`py-[11px] text-[11px] font-bold tracking-[0.6px] uppercase text-slate-400 whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'} ${ci === 0 ? 'pl-5 pr-4' : 'px-4'}`}>{c.label}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {days.map(day => {
                    const closed = day.closeout?.status === 'closed';
                    const isFuture = day.date >= todayKey && !day.hasData;
                    const muted = isFuture || (!day.hasData && !day.isMissing);
                    const busy = mutatingDate === day.date;
                    const exportedOn = fmtStampDate(day.closeout?.exportedAt);
                    return (
                      <tr key={day.date}
                        onClick={() => goToDay(day.date)}
                        className={`border-t border-slate-100 group transition-colors cursor-pointer hover:bg-orange-50/50 ${muted ? 'opacity-45' : ''}`}>
                        <td className="pl-5 pr-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[12.5px] text-slate-700 whitespace-nowrap">{fmtDay(day.date)}</span>
                            {day.adjustmentCount > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-bold whitespace-nowrap" style={{ background: 'rgba(255,122,0,0.10)', color: '#B35500' }}>
                                {day.adjustmentCount} adj
                              </span>
                            )}
                          </div>
                        </td>
                        {['revenue', 'payments', 'tax', ...(hasLedger ? ['ledger'] : [])].map(cat => {
                          const v = displayCategory(cat, Number(day.categories?.[cat] || 0));
                          return (
                            <td key={cat} className="px-4 py-3 text-right font-mono text-[13px] text-slate-700 whitespace-nowrap">
                              {day.hasData || day.adjustmentCount > 0 ? formatCurrency(v) : <span className="text-slate-300">—</span>}
                            </td>
                          );
                        })}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {day.isMissing ? (
                            <span className="inline-flex items-center px-2.5 py-[3px] rounded-full text-[11.5px] font-semibold" style={{ background: DANGER.bg, color: DANGER.ink }}>Missing data</span>
                          ) : !day.hasData && day.adjustmentCount === 0 ? (
                            <span className="text-[13px] text-slate-300">—</span>
                          ) : day.balanced ? (
                            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: SUCCESS.ink }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                              Balanced
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 font-mono text-[12.5px] font-bold" style={{ color: DANGER.ink }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.7 1.8 18a1 1 0 0 0 .9 1.5h18.6a1 1 0 0 0 .9-1.5L13.7 3.7a1 1 0 0 0-1.7 0Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                              Off by {formatCurrency(-day.net)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {(day.hasData || day.adjustmentCount > 0 || closed) ? <StatusChip closeout={day.closeout} /> : <span className="text-[13px] text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {closed && day.closeout?.closedBy ? (
                            <div>
                              <div className="text-[12.5px] font-medium text-slate-600">{day.closeout.closedBy}</div>
                              {fmtStampDate(day.closeout.closedAt) && <div className="text-[11px] text-slate-400">{fmtStampDate(day.closeout.closedAt)}</div>}
                            </div>
                          ) : (
                            <span className="text-[13px] text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {exportedOn ? (
                            <span className="inline-flex items-center px-2.5 py-[3px] rounded-full text-[11.5px] font-semibold" style={{ background: '#F0F9FF', color: '#0369A1' }}>Exported {exportedOn}</span>
                          ) : (
                            <span className="text-[13px] text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-4 pr-5 py-3 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-1.5">
                            {closed ? (
                              isOrgAdmin && (
                                <button onClick={() => handleReopenDay(day.date)} disabled={mutatingDate !== null}
                                  className="h-[30px] px-[11px] inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-600 rounded-lg text-[12.5px] font-semibold opacity-70 group-hover:opacity-100 transition-opacity hover:bg-slate-50 disabled:opacity-40">
                                  <LockOpen size={12} /> {busy ? 'Reopening…' : 'Reopen'}
                                </button>
                              )
                            ) : (canWriteJournal && (day.hasData || day.adjustmentCount > 0) && day.date < todayKey) ? (
                              day.balanced ? (
                                <button onClick={() => handleCloseDay(day.date)} disabled={mutatingDate !== null}
                                  className="h-[30px] px-[11px] inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-700 rounded-lg text-[12.5px] font-semibold opacity-70 group-hover:opacity-100 transition-opacity hover:bg-slate-50 disabled:opacity-40">
                                  <Lock size={12} /> {busy ? 'Closing…' : 'Close day'}
                                </button>
                              ) : (
                                <span title="An out-of-balance day cannot be closed — balance the journal first"
                                  className="h-[30px] px-2 inline-flex items-center text-[11.5px] font-semibold text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                  Balance to close
                                </span>
                              )
                            ) : null}
                            <button onClick={() => goToDay(day.date)} title="Open this day in the detail view"
                              className="h-[30px] w-[30px] inline-flex items-center justify-center border border-slate-200 bg-white text-slate-400 rounded-lg opacity-70 group-hover:opacity-100 transition-opacity hover:text-orange-500 hover:border-orange-200">
                              <ArrowRight size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 bg-white">
                      <td className="pl-5 pr-4 py-3.5">
                        <span className="text-[11px] font-bold uppercase tracking-[0.6px] text-slate-500">Month total · {totals.dataDays} day{totals.dataDays === 1 ? '' : 's'} of data</span>
                      </td>
                      {['revenue', 'payments', 'tax', ...(hasLedger ? ['ledger'] : [])].map(cat => (
                        <td key={cat} className="px-4 py-3.5 text-right font-mono text-[13px] font-bold text-slate-900 whitespace-nowrap">
                          {formatCurrency(displayCategory(cat, Number(totals.categories?.[cat] || 0)))}
                        </td>
                      ))}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {Math.abs(totals.net) <= 0.02 ? (
                          <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold" style={{ color: SUCCESS.ink }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            Balanced
                          </span>
                        ) : (
                          <span className="font-mono text-[12.5px] font-bold" style={{ color: DANGER.ink }}>Off by {formatCurrency(-totals.net)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-[12.5px] font-semibold text-slate-500 whitespace-nowrap">{totals.closedDays} closed</td>
                      <td className="px-4 py-3.5" />
                      <td className="px-4 py-3.5" />
                      <td className="px-4 pr-5 py-3.5" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JournalOverviewPage;
